import { setPostHogPersonMetadata, capturePostHogEvent } from 'client/lib/posthog'
import type { PostHogUser } from 'client/lib/posthog'
import { PLANS } from '../../server/lib/constants'
import sleep from 'lib/sleep'
import * as sentry from 'client/lib/sentry'
import isEqual from 'lodash.isequal'
import { useEffect, useRef } from 'react'

const IS_DEV = process.env.NODE_ENV === 'development'
const GA_DISABLED = !CONFIG.googleAnalytics.enabled
const PH_DISABLED = !CONFIG.posthog.enabled

export interface AnalyticsEvent {
  action: string
  category?: string
  label?: string
  member_id?: string | number
  [key: string]: unknown
}

interface AnalyticsUser extends PostHogUser {
  slug?: string
  name?: string
}

export const setup = async (cookiePreference: string): Promise<void> => {
  if (typeof window === 'undefined') {
    return
  }

  const gpcValue = window.navigator.globalPrivacyControl

  if (GA_DISABLED || gpcValue) return

  while (true) {
    if (!window.gtag) {
      await sleep(1000)
      continue
    }

    if (cookiePreference === 'accepted' || cookiePreference.split(',').includes('targeting')) {
      gtag('consent', 'update', {
        ad_storage: 'granted',
        analytics_storage: 'granted'
      })
    } else {
      // Explicit revoke, not just omission — consent mode only reacts to `update` calls, so a
      // prior `granted` (e.g. `setup` ran once before we knew a visitor was FERPA-covered) stays
      // granted forever unless we say otherwise here.
      gtag('consent', 'update', {
        ad_storage: 'denied',
        analytics_storage: 'denied'
      })
    }

    gtag('config', CONFIG.googleAnalytics.ga4id)
    break
  }
}

export const pageView = (): void => {
  const serverSideRendering = typeof window === 'undefined'
  if (serverSideRendering) return

  if (GA_DISABLED) return
  if (typeof gtag === 'undefined') {
    return
  }
  gtag('event', 'page_view', {
    page_path: window.location.pathname,
    page_location: String(window.location)
  })
}

export const modalView = (modal: string): void => {
  const serverSideRendering = typeof window === 'undefined'
  if (serverSideRendering) return

  if (GA_DISABLED) return
  if (typeof gtag === 'undefined') {
    return
  }
  gtag('event', 'page_view', {
    page_path: modal
  })
}

export const SIGNUP_METHODS: Record<string, string> = {
  'Created an Account': 'local',

  'Logged in - Orcid': 'orcid',
  'Signed up - Orcid': 'orcid',

  'Logged in - Facebook': 'facebook',
  'Signed up - Facebook': 'facebook',

  'Logged in - Google': 'google',
  'Signed up - Google': 'google',

  'Logged in - Saml SSO': 'saml',
  'Signed up - Saml SSO': 'saml'
}

// These indicate a user has an account
// sometimes they will have an account from the past
// and are just signing in which is ok.
export const SIGNUP_ACTIONS = {
  CREATED_ACCOUNT: 'Created an Account',

  LOGGED_IN_ORCID: 'Logged in - Orcid',
  SIGNED_UP_ORCID: 'Signed up - Orcid',

  LOGGED_IN_FACEBOOK: 'Logged in - Facebook',
  SIGNED_UP_FACEBOOK: 'Signed up - Facebook',

  LOGGED_IN_GOOGLE: 'Logged in - Google',
  SIGNED_UP_GOOGLE: 'Signed up - Google',

  LOGGED_IN_SAML_SSO: 'Logged in - Saml SSO',
  SIGNED_UP_SAML_SSO: 'Signed up - Saml SSO'
}

// A sign-up attempt rejected client-side before it reached the API. Must stay
// OUT of SIGNUP_ACTIONS: pixelTrack reports everything in there to Google Ads,
// Facebook and OpenAI as a completed signup conversion. See SCI-3609.
export const SIGNUP_BLOCKED_ACTION = 'Signup Blocked'

// These are predefined facebook events found: https://developers.facebook.com/docs/facebook-pixel/reference#standard-events
const FACEBOOK_EVENTS = {
  COMPLETE_REGISTRATION: 'CompleteRegistration',
  SUBSCRIBE: 'Subscribe',
  INITIATE_CHECKOUT: 'InitiateCheckout',
  START_TRIAL: 'StartTrial'
}

// These are OpenAI / ChatGPT ads conversion events
const OPENAI_EVENTS = {
  REGISTRATION_COMPLETED: 'registration_completed'
}

function bingReportSubscribeConversion(): void {
  const serverSideRendering = typeof window === 'undefined'
  if (serverSideRendering) return

  if (GA_DISABLED) return
  if (typeof window.uetq !== 'undefined') {
    window.uetq = window.uetq || []
    window.uetq.push('event', 'subscribe', {})
  }
}

// pixelTrack will send events to a given ad pixel to build audiences
// in the respective ad platforms.
const pixelTrack = (event: AnalyticsEvent): void => {
  let gtagType: string | undefined
  let facebookType: string | undefined
  let openaiType: string | undefined
  if (Object.values(SIGNUP_ACTIONS).includes(event.action)) {
    gtagType = CONFIG.gtag.signup
    facebookType = FACEBOOK_EVENTS.COMPLETE_REGISTRATION
    openaiType = OPENAI_EVENTS.REGISTRATION_COMPLETED
  } else if (event.action === 'Purchased Plan') {
    gtagType = CONFIG.gtag.subscribeToPremium
    bingReportSubscribeConversion()
    facebookType = FACEBOOK_EVENTS.SUBSCRIBE
  } else if (event.action === 'View Checkout') {
    gtagType = CONFIG.gtag.viewCheckout
    facebookType = FACEBOOK_EVENTS.INITIATE_CHECKOUT
  } else if (event.action === 'View Pricing Page') {
    gtagType = CONFIG.gtag.viewPricingPage
    facebookType = FACEBOOK_EVENTS.INITIATE_CHECKOUT
  } else if (event.action === 'Activated Trial') {
    bingReportSubscribeConversion()
    facebookType = FACEBOOK_EVENTS.START_TRIAL
  } else {
    return
  }
  if (typeof gtag !== 'undefined') {
    gtag('event', 'conversion', {
      send_to: gtagType
    })
  }
  if (typeof fbq !== 'undefined') {
    fbq('track', facebookType)
  }
  if (openaiType && typeof oaiq !== 'undefined') {
    oaiq('measure', openaiType, {
      type: 'customer_action',
      amount: 0,
      currency: 'USD'
    })
  }
}

export const event = (event: AnalyticsEvent, { pushToPosthog = true }: { pushToPosthog?: boolean } = {}): void => {
  if (IS_DEV) {
    console.log('event:', event)
  }

  sendEventGoogleAnalytics(event)
  sendEventPostHog(event, pushToPosthog)
}

const sendEventGoogleAnalytics = (event: AnalyticsEvent): void => {
  const serverSideRendering = typeof window === 'undefined'
  if (serverSideRendering) return

  if (GA_DISABLED) return

  const { action } = event

  const gtagEvent: Record<string, unknown> = {}
  if (event.category) {
    gtagEvent.event_category = event.category
  }
  if (event.label) {
    gtagEvent.event_label = event.label
  }
  if (event.member_id) {
    gtagEvent.member_id = event.member_id
  }
  if (window.gtag) {
    gtag('event', event.action, gtagEvent)
  }

  pixelTrack(event)

  // add sign up conversion event
  // with oauth we treat logins as sign ups
  // analytics can dedupe these into actual sign ups
  if (Object.values(SIGNUP_ACTIONS).includes(action)) {
    if (window.gtag) {
      gtag('event', 'sign_up', {
        method: SIGNUP_METHODS[action]
      })
    }
  }
}

const sendEventPostHog = (event: AnalyticsEvent, pushToPosthog: boolean): void => {
  const serverSideRendering = typeof window === 'undefined'
  if (serverSideRendering) return

  if (PH_DISABLED || !pushToPosthog) return

  capturePostHogEvent({
    name: `${event.category}: ${event.action}`,
    metadata: event
  })
}

// This is for events that are custom GA hooks like purchasing
export const rawGtag = (...args: unknown[]): void => {
  const serverSideRendering = typeof window === 'undefined'
  if (serverSideRendering) return

  if (GA_DISABLED) return
  if (!window.gtag) {
    return
  }
  gtag(...args)
}

const setUserGA = (user: AnalyticsUser): void => {
  const serverSideRendering = typeof window === 'undefined'
  if (serverSideRendering) return

  if (GA_DISABLED) return
  if (!window.gtag) {
    return
  }
  gtag('set', 'user_properties', {
    plan: user.subscription?.plan || PLANS.free,
    subscriptionStatus: user.subscription?.status || 'inactive',
    orcid: user.orcid,
    signupType: user.signupType,
    persona: user.persona || user.userSpecifiedPersona || null,
    organizationLicense: user.organizationLicense
  })
}

let previouslySetUser: AnalyticsUser = {}

// setUser is a universal analytics function
// that captures setting a user's analytics in all analytics apps
// this is the only function that should be used to set up a user in analytics
export const setUser = (user: AnalyticsUser): void => {
  if (isEqual(user, previouslySetUser)) {
    console.debug('User already set')
    return
  }
  previouslySetUser = { ...user }
  setPostHogPersonMetadata(user)
  sentry.setUser(user)
  setUserGA(user)
}

export const KeyActions = {
  Assistant: {
    ViewPage: 'View Page',
    EnterQuery: 'Enter Query',
    ResponseLoad: 'Response Load',
    ResponseCompletion: 'Response Completion',
    ResponseError: 'Response Error',
    Exit: 'Exit',
    SearchStrategyCompletion: 'Search Strategy Completion',
    ReferenceCompletion: 'Reference Completion',
    FollowUpQuery: 'Follow Up Query',
    TimeSpent: 'Time Spent',
    ResponseEditing: 'Response Editing'
  }
}

/**
 * Report a key action to posthog
 */
export const keyAction = (keyAction: string, metadata: Record<string, unknown> | null = null): void => {
  const eventName = keyPath(KeyActions as Record<string, unknown>, keyAction)?.join(' | ')
  if (!eventName) {
    console.error(`Unknown key action: ${keyAction}`)
    return
  }
  const eventData: { name: string; metadata?: Record<string, unknown> } = {
    name: eventName
  }
  if (metadata) {
    eventData.metadata = metadata
  }
  if (IS_DEV) {
    console.log('Key Action:', eventData)
  }
  capturePostHogEvent(eventData)
}

/**
 * Create a hierarchy of the key action and its categories
 */
function keyPath(obj: Record<string, unknown>, value: string, path: string[] = []): string[] | undefined {
  for (const [k, v] of Object.entries(obj)) {
    if (v === value) {
      return [k, value]
    }
    if (typeof v === 'object' && v) {
      const subResult = keyPath(v as Record<string, unknown>, value, [...path, k])
      if (subResult?.length) {
        return [k, ...subResult]
      }
    }
  }
}

/**
 * Report a key action to posthog when loading changes from true to false
 */
export const useKeyActionLoading = (action: string, loading: boolean, callback?: (seconds: number) => void): void => {
  const loadingStartTime = useRef<number | null>(null)
  useEffect(() => {
    if (loading) {
      loadingStartTime.current = Date.now()
    } else if (loadingStartTime.current) {
      const elapsedSeconds = Math.round((Date.now() - loadingStartTime.current) / 1000)
      keyAction(action)
      callback?.(elapsedSeconds)
    }
  }, [loading])
}

/**
 * Tracks how much time the component is mounted
 */
export const useKeyActionTimeSpent = (callback: (seconds: number) => void): void => {
  const startTime = useRef(Date.now())
  useEffect(() => {
    return () => {
      const elapsedSeconds = Math.round((Date.now() - startTime.current) / 1000)
      callback(elapsedSeconds)
    }
  }, [])
}
